// 字典／筆順浮層的掛載點：任何地方呼叫 openDict(字) 就會開啟，不需要經過 App 的狀態
// 全螢幕浮層，不切換頁面（測驗中點題目裡的英文字不會中斷測驗）
// 另外在每個頁面右側放一顆小浮動按鈕，隨時展開「字典／筆順」
import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Dictionary from './Dictionary';

const StrokeOrder = lazy(() => import('./StrokeOrder'));

const EVENT = 'gsat-dict';
export const openDict = (word = '') => window.dispatchEvent(new CustomEvent<string>(EVENT, { detail: word }));

const fab: React.CSSProperties = {
  width: 44, height: 44, borderRadius: 99, border: '1.5px solid var(--border)', background: 'var(--card)',
  color: 'var(--text)', fontSize: 20, cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,.25)', padding: 0, lineHeight: 1,
};

const POS_KEY = 'gsat_fab_pos';
const SIZE = 44;
const readPos = (): { x: number; y: number } | null => {
  try { const v = JSON.parse(localStorage.getItem(POS_KEY) || 'null'); if (v && typeof v.x === 'number' && typeof v.y === 'number') return v; } catch { /* 無痕模式等 */ }
  return null;
};
// 預設位置：手機貼右側；電腦版靠近置中的內容欄（約 520px 寬）旁邊，不要飄到視窗最右邊
const defaultPos = () => {
  const w = window.innerWidth, h = window.innerHeight;
  const x = w > 700 ? Math.min(w - SIZE - 10, w / 2 + 300) : w - SIZE - 10;
  return { x, y: Math.round(h * 0.38) };   // 偏上，不會被底部導覽列或手機網址列擋住
};
const clampPos = (p: { x: number; y: number }) => ({
  x: Math.max(4, Math.min(window.innerWidth - SIZE - 4, p.x)),
  y: Math.max(4, Math.min(window.innerHeight - SIZE - 4, p.y)),
});

export default function DictHost() {
  const [pos, setPos] = useState<{ x: number; y: number }>(() => clampPos(readPos() ?? defaultPos()));
  const drag = useRef<{ dx: number; dy: number; moved: boolean } | null>(null);
  useEffect(() => {
    const onResize = () => setPos(p => clampPos(p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [word, setWord] = useState<string | null>(null);
  const [stroke, setStroke] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  const [nonce, setNonce] = useState(0);   // 重複點同一個字也要重新查
  useEffect(() => {
    const h = (e: Event) => { setWord((e as CustomEvent<string>).detail ?? ''); setNonce(n => n + 1); };
    window.addEventListener(EVENT, h);
    return () => window.removeEventListener(EVENT, h);
  }, []);
  const layerOpen = word !== null || stroke !== null;
  const lower = pos.y > window.innerHeight / 2;
  return (
    <>
      {word !== null && createPortal(<Dictionary key={nonce} initial={word} onClose={() => setWord(null)} />, document.body)}
      {stroke !== null && createPortal(
        <Suspense fallback={null}><StrokeOrder initial={stroke} onClose={() => setStroke(null)} /></Suspense>, document.body)}
      {!layerOpen && createPortal(
        <div style={{ position: 'fixed', left: pos.x, width: SIZE, zIndex: 9000, display: 'flex', alignItems: 'center', gap: 8, touchAction: 'none',
          // 在下半部時改以底邊定位、選單往上展開；在上半部則往下展開，選單不會超出螢幕
          ...(lower ? { bottom: window.innerHeight - pos.y - SIZE, flexDirection: 'column' } : { top: pos.y, flexDirection: 'column-reverse' }) }}>
          {menu && (
            <>
              <button aria-label="開啟字典" title="字典" style={fab} onClick={() => { setMenu(false); openDict(); }}>📖</button>
              <button aria-label="開啟筆順" title="筆順" style={fab} onClick={() => { setMenu(false); setStroke(''); }}>✍️</button>
            </>
          )}
          <button aria-label={menu ? '收起工具' : '展開字典與筆順'} title="字典／筆順（可拖曳移動）"
            onPointerDown={e => { drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y, moved: false }; (e.target as HTMLElement).setPointerCapture?.(e.pointerId); }}
            onPointerMove={e => {
              const d = drag.current; if (!d) return;
              const np = clampPos({ x: e.clientX - d.dx, y: e.clientY - d.dy });
              if (Math.abs(np.x - pos.x) + Math.abs(np.y - pos.y) > 3) d.moved = true;
              if (d.moved) setPos(np);
            }}
            onPointerUp={() => {
              const d = drag.current; drag.current = null;
              if (d?.moved) { try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* 忽略 */ } }
              else setMenu(m => !m);
            }}
            style={{ ...fab, opacity: menu ? 1 : 0.7, fontSize: menu ? 18 : 20, touchAction: 'none' }}>{menu ? '✕' : '🔤'}</button>
        </div>, document.body)}
    </>
  );
}
